import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import DifmBannerModal from '../components/difm-banner-modal';
import DifmBannerSelectItem from '../components/difm-banner-select-item';
import packagesOptions from '../constants/packages-options';
import servicesOptions from '../constants/services-options';
import prepareDifmData from '../helpers/prepare-difm-data';
import yousersServiceClient from '../../common/helpers/yousers-service-client';

const { Stack, Paragraph, ControlGroup, NotificationMessage } = designSystem;

const modalCaptions = {
  subtitle: 'Select your package now to indicate your desired service, without immediate charges ',
  description:
    '* The prices provided are not final and may be changed as we work together to plan the ideal website for you. You will not be charged until we sort out all the details.',
  back: 'Back',
  next: 'Confirm',
  errorMessage: 'Oops! Something went wrong. Please try again later.',
};

const DifmBannerPackagesContainer = ({ values, onBack, onClose, onNext, onSelect }) => {
  const { serviceName } = values;
  const options = packagesOptions[serviceName];
  const isPackageFitService = options.some(({ value }) => value === values.selectedPackage);

  const [selectedPackage, setSelectedPackage] = useState(
    isPackageFitService ? values.selectedPackage : ''
  );
  const [loading, setLoading] = useState(false);
  const [showError, setShowError] = useState(false);

  const onSelectItemClick = (value) => {
    setSelectedPackage(value);
    onSelect(value);
  };
  const clearError = () => setShowError(false);
  const onCloseErrorClick = () => clearError();
  const handleBack = () => onBack({ ...values, selectedPackage });
  const handleNext = () => {
    setLoading(true);
    clearError();

    const preparedData = prepareDifmData({ ...values, selectedPackage });
    const yousers = yousersServiceClient.get();

    yousers.requests
      .submitDifm(preparedData)
      .then(() => onNext({ ...values, selectedPackage }))
      .catch(() => {
        setShowError(true);
      })
      .finally(() => setLoading(false));
  };

  const title = useMemo(() => servicesOptions.find(({ value }) => value === serviceName).title, [
    serviceName,
  ]);

  return (
    <DifmBannerModal
      isLoading={loading}
      onNext={handleNext}
      isNextDisabled={!selectedPackage}
      onBack={handleBack}
      onClose={onClose}
      captions={{ ...modalCaptions, title }}
    >
      <ControlGroup title={modalCaptions.subtitle} required>
        <Stack gap="spacing-2-xs">
          {options.map(({ value, ...restProps }) => (
            <DifmBannerSelectItem
              key={value}
              active={value === selectedPackage}
              value={value}
              {...restProps}
              onClick={onSelectItemClick}
            />
          ))}
          <Paragraph appearance="medium-emphasis" size="small">
            {modalCaptions.description}
          </Paragraph>
          {showError && (
            <NotificationMessage
              appearance="danger"
              iconGlyph="attention"
              title={
                // eslint-disable-next-line yola/react/jsx-wrap-multilines
                <Paragraph appearance="danger">
                  <b>{modalCaptions.errorMessage}</b>
                </Paragraph>
              }
              onClose={onCloseErrorClick}
            />
          )}
        </Stack>
      </ControlGroup>
    </DifmBannerModal>
  );
};

DifmBannerPackagesContainer.propTypes = {
  values: PropTypes.shape({
    selectedPackage: PropTypes.string,
    siteName: PropTypes.string,
    serviceName: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default DifmBannerPackagesContainer;
